import { ApolloClient, InMemoryCache, from } from "@apollo/client";
import { createUploadLink } from "apollo-upload-client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { localStorageKeys } from "@/services/storage";
import { ROUTES } from "::root/src/consts";

const httpLink = createUploadLink({
	uri: import.meta.env.VITE_API_URL,
});

const authLink = setContext((_, { headers }) => {
	// get the authentication token from local storage if it exists
	const token = localStorage.getItem(localStorageKeys.token);
	// return the headers to the context so httpLink can read them
	return {
		headers: {
			...headers,
			authorization: token ? `Bearer ${token}` : "",
		},
	};
});

/**
 * GraphQL operations where a 401 should not clear the whole session.
 * Some resolvers return 401 for field-level / policy issues while the token
 * is still valid for the rest of the app; clearing storage would log the user out incorrectly.
 */
const AUTH_LOGOUT_EXCLUDED_OPERATIONS = new Set([
	"GetOpportunities",
	"GetOpportunity",
	"CreateOpportunity",
	"UpdateOpportunity",
]);

// acts as an interceptor for all requests, and checks for a 401 status code.
const logoutLink = onError(({ graphQLErrors, operation }) => {
	const opName = operation.operationName;
	if (opName && AUTH_LOGOUT_EXCLUDED_OPERATIONS.has(opName)) {
		return;
	}
	if (graphQLErrors)
		graphQLErrors.forEach((graphQLError) => {
			// performing this typeguard check because the statusCode is not always present, and its not part of the GraphQLError type definition
			if (
				graphQLError &&
				"statusCode" in graphQLError &&
				graphQLError.statusCode === 401
			) {
				localStorage.clear();
				window.location.replace(ROUTES.Login);
			}
		});
});

const client = new ApolloClient({
	link: from([logoutLink, authLink.concat(httpLink)]),
	cache: new InMemoryCache(),
});

export default client;
